import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { onboardingKeys } from '@/data/query-keys';
import { OnboardingHeader } from '@/features/onboarding/OnboardingHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle2, Circle, Building2, CreditCard, Package, Smartphone, ArrowRight, Home, Settings, Calculator, Lock, ChefHat, RotateCcw, Loader2, DollarSign } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { resetModuleData, MODULE_RESET_CONFIG, type ResetModuleId } from '@/lib/onboardingDataReset';

interface ModuleStatus {
  status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
  completedAt?: string;
  percentComplete?: number;
}

export const OnboardingHub = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [modules, setModules] = useState<Record<string, ModuleStatus>>({});
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [resetModuleId, setResetModuleId] = useState<ResetModuleId | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const loadProgress = useCallback(async (): Promise<Record<string, ModuleStatus> | null> => {
    try {
      const fetchedCompanyId = await getCurrentUserCompanyId();
      if (!fetchedCompanyId) {
        setLoading(false);
        return null;
      }
      setCompanyId(fetchedCompanyId);

      // Load onboarding progress
      const { data, error } = await supabase
        .from('onboarding_progress')
        .select('sections_completed, completed_at, last_activity')
        .eq('company_id', fetchedCompanyId)
        .single();

      if (error) {
        console.error('Error loading onboarding progress:', error);
        setLoading(false);
        return null;
      }

      const sectionsCompleted = (data?.sections_completed as any) || {};
      const originalSections = JSON.parse(JSON.stringify(sectionsCompleted)); // Deep copy for comparison
      const phase2Sections = sectionsCompleted.phase2 || {};

      // Fetch onboarding data in batches to avoid deep type instantiation
      // Batch 1: Company and counts  
      const companyQuery = supabase.from('companies').select('*').eq('id', fetchedCompanyId).single();
      const vendorsQuery = supabase.from('vendors').select('id', { count: 'exact', head: true }).eq('company_id', fetchedCompanyId);
      const bankAccountsQuery = supabase.from('bank_accounts').select('id', { count: 'exact', head: true }).eq('company_id', fetchedCompanyId).eq('is_active', true);
      const locationsQuery = supabase.from('locations').select('*').eq('company_id', fetchedCompanyId);
      
      const [companyResult, vendorsCountResult, bankAccountsCountResult, locationsResult] = await Promise.all([
        companyQuery,
        vendorsQuery,
        bankAccountsQuery,
        locationsQuery
      ]);

      // Batch 2: Inventory counts
      // @ts-ignore - Supabase types cause deep type instantiation
      const [menuCountResult, prepCountResult, ingredientsCountResult, payrollSetupResult] = await Promise.all([
        supabase.from('menu_items').select('id', { count: 'exact', head: true }).eq('company_id', fetchedCompanyId),
        supabase.from('prep_items').select('id', { count: 'exact', head: true }).eq('company_id', fetchedCompanyId),
        supabase.from('ingredients').select('id', { count: 'exact', head: true }).eq('company_id', fetchedCompanyId),
        supabase.from('payroll_setup').select('*').eq('company_id', fetchedCompanyId).maybeSingle()
      ]);

      // Extract data from results
      const company = companyResult.data;
      const counts = {
        vendors: vendorsCountResult.count || 0,
        bank_accounts: bankAccountsCountResult.count || 0,
        menu_items: menuCountResult.count || 0,
        prep_items: prepCountResult.count || 0,
        ingredients: ingredientsCountResult.count || 0
      };
      const locations = locationsResult.data || [];
      
      // Training progress is already available in sectionsCompleted
      const trainingProgress = {
        bank_accounts: (sectionsCompleted.bank_accounts as any)?.status === 'completed',
        cogs_vendor: (sectionsCompleted.vendors as any)?.status === 'completed',
        pos: (sectionsCompleted.pos_integration as any)?.status === 'completed'
      };

      // Normalize data structure  
      const companyData = { data: company, error: company ? null : { message: 'Company not found' } };
      const vendorsCount = { count: counts.vendors };
      const bankAccountsCount = { count: counts.bank_accounts };
      const locationsData = { data: locations };
      const menuCount = { count: counts.menu_items };
      const prepCount = { count: counts.prep_items };
      const ingredientsCount = { count: counts.ingredients };
      const bankAccountTrainingData = { count: trainingProgress.bank_accounts ? 1 : 0 };
      const cogsVendorTrainingData = { count: trainingProgress.cogs_vendor ? 1 : 0 };
      const posTrainingData = { data: trainingProgress.pos ? [{ id: 'exists' }] : [] };

      // Validate Business Profile: Check all required fields
      const isBusinessProfileComplete = () => {
        const company = companyData.data;
        if (!company || companyData.error) return false;
        
        // Check required company fields (handle case where some columns may not exist)
        const hasRequiredCompanyFields = 
          company.name && 
          company.restaurant_name && 
          company.entity_type;
        // Note: rep_name, rep_title, email, phone may not be required columns
        
        if (!hasRequiredCompanyFields) return false;
        
        // Check for at least one complete location
        const locations = locationsData.data || [];
        const hasCompleteLocation = locations.some((loc: any) => 
          loc.location_name && 
          loc.address && 
          loc.city && 
          loc.state && 
          loc.zip_code
        );
        
        return hasCompleteLocation;
      };

      // Validate Bank Accounts: Need at least one active bank account
      const isBankAccountsComplete = () => {
        return (bankAccountsCount.count || 0) > 0;
      };

      // Validate Inventory Setup: Need ingredients
      const isInventorySetupComplete = () => {
        return (ingredientsCount.count || 0) > 0;
      };

      // Validate Prep & Menu Recipes: Need prep items OR menu items
      const isPrepMenuRecipesComplete = () => {
        const hasMenuItems = (menuCount.count || 0) > 0;
        const hasPrepItems = (prepCount.count || 0) > 0;
        return hasMenuItems || hasPrepItems;
      };

      // Validate Bank Account Training: Need bank accounts + training progress
      const isBankAccountTrainingComplete = () => {
        const hasBankAccounts = isBankAccountsComplete();
        const hasTrainingProgress = (bankAccountTrainingData.count || 0) > 0;
        return hasBankAccounts && hasTrainingProgress;
      };

      // Validate COGS Vendor Training: Need vendors + training progress
      const isCogsVendorTrainingComplete = () => {
        const hasVendors = (vendorsCount.count || 0) > 0;
        const hasTrainingProgress = (cogsVendorTrainingData.count || 0) > 0;
        return hasVendors && hasTrainingProgress;
      };

      // Validate POS Training: Need pos integration + training progress
      const isPosTrainingComplete = () => {
        // Check if pos_integration section is completed or if training progress exists
        const hasIntegration = sectionsCompleted.pos_integration?.status === 'completed';
        const hasTrainingProgress = posTrainingData.data && posTrainingData.data.length > 0;
        return hasIntegration && hasTrainingProgress;
      };

      // Validate Payroll: Check if payroll_setup record exists with required fields
      const isPayrollComplete = () => {
        const payrollData = payrollSetupResult.data;
        if (!payrollData) return false;
        
        // Check required fields are filled
        const hasRequiredFields = 
          payrollData.payroll_frequency && 
          payrollData.location_type;
        
        return hasRequiredFields;
      };

      // Update status based on actual data validation, not just database status
      const businessProfileComplete = isBusinessProfileComplete();
      const bankAccountsComplete = isBankAccountsComplete();
      const inventorySetupComplete = isInventorySetupComplete();
      const prepMenuRecipesComplete = isPrepMenuRecipesComplete();
      const bankAccountTrainingComplete = isBankAccountTrainingComplete();
      const cogsVendorTrainingComplete = isCogsVendorTrainingComplete();
      const posTrainingComplete = isPosTrainingComplete();
      const payrollComplete = isPayrollComplete();

      // Update sections_completed based on data validation
      if (businessProfileComplete) {
        sectionsCompleted.business_profile = { 
          status: 'completed', 
          completedAt: sectionsCompleted.business_profile?.completedAt || new Date().toISOString() 
        };
      } else if (companyData.data || (locationsData.data && locationsData.data.length > 0)) {
        sectionsCompleted.business_profile = { 
          status: 'in_progress',
          ...sectionsCompleted.business_profile 
        };
      }

      if (bankAccountsComplete) {
        sectionsCompleted.bank_accounts = { 
          status: 'completed', 
          completedAt: sectionsCompleted.bank_accounts?.completedAt || new Date().toISOString() 
        };
      } else if ((bankAccountsCount.count || 0) > 0) {
        sectionsCompleted.bank_accounts = { 
          status: 'in_progress',
          ...sectionsCompleted.bank_accounts 
        };
      }

      if (inventorySetupComplete) {
        sectionsCompleted.inventory_list_setup = { 
          status: 'completed', 
          completedAt: sectionsCompleted.inventory_list_setup?.completedAt || new Date().toISOString() 
        };
      } else if ((ingredientsCount.count || 0) > 0) {
        sectionsCompleted.inventory_list_setup = { 
          status: 'in_progress',
          ...sectionsCompleted.inventory_list_setup 
        };
      }

      if (prepMenuRecipesComplete) {
        sectionsCompleted.prep_menu_recipes = { 
          status: 'completed', 
          completedAt: sectionsCompleted.prep_menu_recipes?.completedAt || new Date().toISOString() 
        };
      } else if ((menuCount.count || 0) > 0 || (prepCount.count || 0) > 0) {
        sectionsCompleted.prep_menu_recipes = { 
          status: 'in_progress',
          ...sectionsCompleted.prep_menu_recipes 
        };
      }

      // Update payroll status based on data validation
      if (payrollComplete) {
        sectionsCompleted.payroll = { 
          status: 'completed', 
          completedAt: sectionsCompleted.payroll?.completedAt || new Date().toISOString() 
        };
      } else if (payrollSetupResult.data) {
        // Payroll setup exists but may not be complete
        sectionsCompleted.payroll = { 
          status: 'in_progress',
          ...sectionsCompleted.payroll 
        };
      }

      // Persist status updates to database if any were made
      const wasUpdated = 
        JSON.stringify(sectionsCompleted) !== JSON.stringify(originalSections);

      if (wasUpdated) {
        // Update database with new statuses based on data validation
        await supabase
          .from('onboarding_progress')
          .update({
            sections_completed: sectionsCompleted,
            last_activity: new Date().toISOString(),
          })
          .eq('company_id', fetchedCompanyId);
        queryClient.invalidateQueries({ queryKey: onboardingKeys.progress() });
      }

      // Determine module status based on data validation, not just database status
      // Business Profile: Check status from sections_completed first, then data validation
      const businessProfileStatus = 
        sectionsCompleted.business_profile?.status === 'completed'
          ? 'completed'
          : businessProfileComplete 
          ? 'completed' 
          : (companyData.data || (locationsData.data && locationsData.data.length > 0))
          ? 'in_progress'
          : 'not_started';

      // Bank Account Training: Check status from sections_completed first, then data validation
      const bankAccountTrainingStatus = 
        phase2Sections.bank_accounts_phase2?.status === 'completed' || sectionsCompleted.bank_accounts?.status === 'completed'
          ? 'completed'
          : bankAccountTrainingComplete
          ? 'completed'
          : (bankAccountsComplete || (phase2Sections.bank_accounts_phase2?.status === 'in_progress'))
          ? 'in_progress'
          : 'not_started';

      // COGS Vendor Training: Check data validation
      const cogsVendorTrainingStatus = cogsVendorTrainingComplete
        ? 'completed'
        : ((vendorsCount.count || 0) > 0 || (phase2Sections.cogs_vendor_training?.status === 'in_progress'))
        ? 'in_progress'
        : 'not_started';

      // POS Training: Check data validation
      const posTrainingStatus = posTrainingComplete
        ? 'completed'
        : ((sectionsCompleted.pos_integration?.status === 'completed' || sectionsCompleted.pos_integration?.status === 'in_progress') || 
           (phase2Sections.pos_reports?.status === 'in_progress'))
        ? 'in_progress'
        : 'not_started';

      // Inventory Setup: Check status from sections_completed first, then data validation
      const inventorySetupStatus = 
        sectionsCompleted.inventory_list_setup?.status === 'completed'
          ? 'completed'
          : inventorySetupComplete
          ? 'completed'
          : ((ingredientsCount.count || 0) > 0)
          ? 'in_progress'
          : 'not_started';

      // Prep & Menu Recipes: Use data validation result
      const prepMenuRecipesStatus = prepMenuRecipesComplete
        ? 'completed'
        : ((menuCount.count || 0) > 0 || (prepCount.count || 0) > 0)
        ? 'in_progress'
        : 'not_started';

      const modulesResult: Record<string, ModuleStatus> = {
        business_profile: {
          status: businessProfileStatus as ModuleStatus['status'],
          completedAt: businessProfileComplete 
            ? (sectionsCompleted.business_profile?.completedAt || new Date().toISOString())
            : sectionsCompleted.business_profile?.completedAt,
        },
        payroll: {
          status: sectionsCompleted.payroll?.status === 'completed'
            ? 'completed'
            : payrollComplete
            ? 'completed'
            : payrollSetupResult.data
            ? 'in_progress'
            : 'not_started' as ModuleStatus['status'],
          completedAt: payrollComplete
            ? (sectionsCompleted.payroll?.completedAt || new Date().toISOString())
            : sectionsCompleted.payroll?.completedAt,
        },
        bank_account_training: {
          status: bankAccountTrainingStatus as ModuleStatus['status'],
          completedAt: bankAccountTrainingComplete
            ? (phase2Sections.bank_accounts_phase2?.completedAt || sectionsCompleted.bank_accounts?.completedAt || new Date().toISOString())
            : (phase2Sections.bank_accounts_phase2?.completedAt || sectionsCompleted.bank_accounts?.completedAt),
        },
        cogs_vendor_training: {
          status: cogsVendorTrainingStatus as ModuleStatus['status'],
          completedAt: cogsVendorTrainingComplete
            ? (phase2Sections.cogs_vendor_training?.completedAt || sectionsCompleted.vendors?.completedAt || new Date().toISOString())
            : (phase2Sections.cogs_vendor_training?.completedAt || sectionsCompleted.vendors?.completedAt),
        },
        pos_training: {
          status: posTrainingStatus as ModuleStatus['status'],
          completedAt: posTrainingComplete
            ? (phase2Sections.pos_reports?.completedAt || sectionsCompleted.pos_integration?.completedAt || new Date().toISOString())
            : (phase2Sections.pos_reports?.completedAt || sectionsCompleted.pos_integration?.completedAt),
        },
        inventory_list_setup: {
          status: inventorySetupStatus as ModuleStatus['status'],
          completedAt: inventorySetupComplete
            ? (sectionsCompleted.inventory_list_setup?.completedAt || new Date().toISOString())
            : sectionsCompleted.inventory_list_setup?.completedAt,
        },
        prep_menu_recipes: {
          status: prepMenuRecipesStatus as ModuleStatus['status'],
          completedAt: prepMenuRecipesComplete
            ? (sectionsCompleted.prep_menu_recipes?.completedAt || new Date().toISOString())
            : sectionsCompleted.prep_menu_recipes?.completedAt,
        },
        // ARCHIVED: accounting_addons (addons section) removed for MVP
      };

      setModules(modulesResult);
      setLoading(false);
      return modulesResult;
    } catch (error) {
      console.error('Error loading onboarding progress:', error);
      setLoading(false);
      return null;
    }
  }, [queryClient]);

  // Reload progress when component mounts or when navigating back to this page
  useEffect(() => {
    loadProgress();
  }, [location.pathname, loadProgress]);

  // Use React Query for caching onboarding status
  const { data: cachedStatus } = useQuery({
    queryKey: ['onboarding-status', companyId],
    queryFn: loadProgress,
    staleTime: 15 * 60 * 1000, // 15 minutes - onboarding status doesn't change frequently
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    enabled: !!companyId,
  });

  // Set up real-time subscription to listen for changes (debounced to reduce reloads)
  useEffect(() => {
    let subscription: any = null;
    let cleanupFocus: (() => void) | null = null;
    let reloadTimeout: NodeJS.Timeout | null = null;

    const setupSubscription = async () => {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      // Subscribe to changes in onboarding_progress table with debouncing
      subscription = supabase
        .channel(`onboarding_progress_changes_${companyId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'onboarding_progress',
            filter: `company_id=eq.${companyId}`,
          },
          (payload) => {
            console.log('Onboarding progress changed:', payload);
            // Debounce reload to avoid excessive calls
            if (reloadTimeout) clearTimeout(reloadTimeout);
            reloadTimeout = setTimeout(() => {
              loadProgress();
            }, 500); // Wait 500ms before reloading
          }
        )
        .subscribe();

      // Also reload on window focus (debounced) - in case user made changes in another tab
      const handleFocus = () => {
        if (reloadTimeout) clearTimeout(reloadTimeout);
        reloadTimeout = setTimeout(() => {
          loadProgress();
        }, 1000); // Wait 1 second after focus to avoid unnecessary reloads
      };
      window.addEventListener('focus', handleFocus);
      cleanupFocus = () => {
        window.removeEventListener('focus', handleFocus);
        if (reloadTimeout) clearTimeout(reloadTimeout);
      };
    };

    setupSubscription();

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
      if (cleanupFocus) {
        cleanupFocus();
      }
      if (reloadTimeout) {
        clearTimeout(reloadTimeout);
      }
    };
  }, [loadProgress]);

  const getStatusIcon = (status: ModuleStatus['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'in_progress':
        return <Circle className="h-5 w-5 text-blue-600" />;
      case 'skipped':
        return <Circle className="h-5 w-5 text-gray-400" />;
      default:
        return <Circle className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: ModuleStatus['status']) => {
    switch (status) {
      case 'completed':
        return <span className="text-xs font-medium text-green-600">Completed</span>;
      case 'in_progress':
        return <span className="text-xs font-medium text-blue-600">In Progress</span>;
      case 'skipped':
        return <span className="text-xs font-medium text-gray-500">Skipped</span>;
      default:
        return <span className="text-xs font-medium text-muted-foreground">Not Started</span>;
    }
  };

  // Check if prerequisites are met based on actual data validation
  // TEMPORARILY DISABLED for testing - always returns true
  // TODO: Re-enable prerequisite checking when ready for production
  const isPrerequisiteMet = () => {
    return true; // Disabled for testing
    
    // Original logic (re-enable later):
    // const businessProfileStatus = modules.business_profile?.status;
    // const bankAccountStatus = modules.bank_account_training?.status;
    // const operationsStatus = modules.operations?.status;
    // return (
    //   businessProfileStatus === 'completed' &&
    //   bankAccountStatus === 'completed' &&
    //   operationsStatus === 'completed'
    // );
  };

  const handleResetModule = async () => {
    if (!resetModuleId) return;
    setIsResetting(true);
    try {
      await resetModuleData(resetModuleId);
      toast({
        title: 'Data cleared',
        description: `${MODULE_RESET_CONFIG[resetModuleId].label} data has been cleared. You can start fresh.`,
      });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.progress() });
      await loadProgress();
    } catch (error: any) {
      console.error('Error resetting module data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to clear data. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsResetting(false);
      setResetModuleId(null);
    }
  };

  const resettableModules = new Set<string>(['inventory_list_setup', 'cogs_vendor_training', 'prep_menu_recipes']);

  const moduleConfig = [
    {
      id: 'business_profile',
      title: 'Business Profile',
      description: 'Set up your company profile and basic information',
      icon: Building2,
      route: '/onboarding/business-profile',
      color: 'bg-blue-500/10 text-blue-600',
      isOptional: false,
    },
    {
      id: 'bank_account_training',
      title: 'Bank Account Setup & Training',
      description: 'Set up bank accounts and train AI on transactions',
      icon: CreditCard,
      route: '/onboarding/bank-account-training',
      color: 'bg-emerald-500/10 text-emerald-600',
      isOptional: false,
    },
    {
      id: 'inventory_list_setup',
      title: 'Inventory List Setup',
      description: 'Set up your inventory and ingredient tracking',
      icon: Package,
      route: '/onboarding/inventory-setup',
      color: 'bg-indigo-500/10 text-indigo-600',
      isOptional: false,
    },
    {
      id: 'cogs_vendor_training',
      title: 'COGS Vendor Setup & Training',
      description: 'Set up vendors and train AI to recognize invoices',
      icon: Package,
      route: '/onboarding/cogs-vendor',
      color: 'bg-violet-500/10 text-violet-600',
      isOptional: false,
    },
    {
      id: 'prep_menu_recipes',
      title: 'Prep & Menu Item Recipes',
      description: 'Set up prep items and menu item recipes',
      icon: ChefHat,
      route: '/onboarding/prep-menu-recipes',
      color: 'bg-purple-500/10 text-purple-600',
      isOptional: false,
    },
    {
      id: 'pos_training',
      title: 'POS Integration & Training',
      description: 'Connect POS systems and train AI on reports',
      icon: Smartphone,
      route: '/onboarding/pos-training',
      color: 'bg-orange-500/10 text-orange-600',
      isOptional: false,
    },
    {
      id: 'menu_pricing',
      title: 'Menu Pricing',
      description: 'Set per-platform pricing, campaigns, and review COGS targets',
      icon: DollarSign,
      route: '/onboarding/menu-pricing',
      color: 'bg-teal-500/10 text-teal-600',
      isOptional: false,
    },
    {
      id: 'payroll',
      title: 'Payroll',
      description: 'Configure your payroll settings',
      icon: Building2,
      route: '/onboarding/payroll',
      color: 'bg-pink-500/10 text-pink-600',
      isOptional: false,
    },
    // ARCHIVED: accounting_addons (Addons) removed for MVP
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5">
        <OnboardingHeader />
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          {/* Header Skeleton */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="space-y-2">
                <Skeleton className="h-9 w-64" />
                <Skeleton className="h-5 w-96" />
              </div>
              <Skeleton className="h-10 w-48" />
            </div>
          </div>

          {/* Modules Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 8 }).map((_, index) => (
              <Card key={index} className="h-full">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Skeleton className="h-12 w-12 rounded-lg" />
                    <Skeleton className="h-5 w-5 rounded-full" />
                  </div>
                  <Skeleton className="h-7 w-48 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-12" />
                    </div>
                    <Skeleton className="h-2 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5">
      <OnboardingHeader />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">Onboarding</h1>
              <p className="text-muted-foreground">
                Complete these modules to set up your account
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                sessionStorage.setItem('skip_onboarding_redirect', 'true');
                navigate('/dashboard');
              }}
              className="gap-2"
            >
              <Home className="h-4 w-4" />
              Return to Dashboard
            </Button>
          </div>
        </div>

        {/* Modules Grid */}
        <TooltipProvider>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {moduleConfig.map((module, index) => {
              const moduleStatus = modules[module.id] || { status: 'not_started' as const };
              const Icon = module.icon;
              // TEMPORARILY DISABLED for testing - no modules are disabled
              // TODO: Re-enable when ready: const isDisabled = module.requiresPrerequisites && !isPrerequisiteMet();
              const isDisabled = false;

              const cardElement = (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card
                    className={cn(
                      'h-full transition-all',
                      !isDisabled && 'cursor-pointer hover:shadow-lg',
                      isDisabled && 'opacity-50 cursor-not-allowed',
                      moduleStatus.status === 'completed' && 'border-green-500/20',
                      moduleStatus.status === 'in_progress' && 'border-blue-500/20'
                    )}
                    onClick={() => !isDisabled && navigate(module.route)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <div className={cn('p-3 rounded-lg', module.color, isDisabled && 'opacity-50')}>
                          <Icon className="h-6 w-6" />
                        </div>
                        <div className="flex items-center gap-2">
                          {isDisabled && <Lock className="h-4 w-4 text-muted-foreground" />}
                          {getStatusIcon(moduleStatus.status)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <CardTitle className={cn('text-xl', isDisabled && 'text-muted-foreground')}>
                          {module.title}
                        </CardTitle>
                        {module.isOptional && (
                          <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            Optional
                          </span>
                        )}
                      </div>
                      <CardDescription className={cn(isDisabled && 'text-muted-foreground')}>
                        {module.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          {getStatusBadge(moduleStatus.status)}
                          {moduleStatus.percentComplete !== undefined && (
                            <span className="text-sm text-muted-foreground">
                              {moduleStatus.percentComplete}%
                            </span>
                          )}
                        </div>
                        {moduleStatus.percentComplete !== undefined && (
                          <Progress value={moduleStatus.percentComplete} className="h-2" />
                        )}
                        {isDisabled && (
                          <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                            Complete Business Profile, Bank Account Setup, and Operations to unlock
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button
                            variant={moduleStatus.status === 'completed' ? 'outline' : 'default'}
                            className="flex-1 gap-2"
                            disabled={isDisabled}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isDisabled) {
                                navigate(module.route);
                              }
                            }}
                          >
                            {moduleStatus.status === 'completed' ? 'Review' : 'Continue'}
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                          {resettableModules.has(module.id) && moduleStatus.status !== 'not_started' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setResetModuleId(module.id as ResetModuleId);
                                  }}
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Clear data &amp; start fresh</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );

              if (isDisabled) {
                return (
                  <Tooltip key={module.id}>
                    <TooltipTrigger asChild>
                      {cardElement}
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Complete Business Profile, Bank Account Setup & Training, and Operations to access this module.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return cardElement;
            })}
          </div>
        </TooltipProvider>
      </div>

      <AlertDialog open={!!resetModuleId} onOpenChange={(open) => { if (!open && !isResetting) setResetModuleId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Clear {resetModuleId ? MODULE_RESET_CONFIG[resetModuleId].label : ''} Data?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {resetModuleId ? MODULE_RESET_CONFIG[resetModuleId].description : ''}
              {' '}This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetModule}
              disabled={isResetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isResetting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Clearing...
                </>
              ) : (
                'Clear All Data'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

